// src/pages/Login.js
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signInWithGoogle, signInWithFacebook, signInWithEmail, registerWithEmail } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/portal";

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSocial = async (provider) => {
    setError(""); setLoading(true);
    try {
      await (provider === "google" ? signInWithGoogle() : signInWithFacebook());
      navigate(redirect);
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  };

  const handleEmail = async e => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") {
        await signInWithEmail(form.email, form.password);
      } else {
        if (!form.name.trim()) throw new Error("Please enter your full name.");
        await registerWithEmail(form.email, form.password, form.name);
      }
      navigate(redirect);
    } catch (e) {
      let errorMessage = "An unexpected error occurred. Please try again.";
      switch (e.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          errorMessage = "Invalid email or password.";
          break;
        case 'auth/email-already-in-use':
          errorMessage = "An account with this email already exists.";
          break;
        case 'auth/weak-password':
          errorMessage = "Password should be at least 6 characters.";
          break;
        default:
          errorMessage = e.message;
      }
      setError(errorMessage);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ paddingTop: "var(--header-h)", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden", padding: "calc(var(--header-h) + 40px) 24px 60px" }}>
      
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ width: "100%", maxWidth: 420, position: "relative", zIndex: 1 }}
      >
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 10, color: "var(--text)", marginBottom: 24, fontFamily: "var(--font-head)" }}>
            <div style={{ width: 36, height: 36, background: "var(--accent)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, color: "var(--navy)" }}>A</div>
            <span style={{ fontWeight: 700, letterSpacing: "0.02em" }}>AIMBLE I.T. SOLUTIONS</span>
          </Link>
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700, fontFamily: "var(--font-head)", marginBottom: 8 }}>
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p style={{ fontSize: "0.875rem", color: "var(--text-muted)" }}>
            {mode === "login" ? "Sign in to access your client portal" : "Create an account to manage your projects"}
          </p>
        </div>

        <div style={{ background: "var(--surface)", border: "1px solid var(--border-med)", borderRadius: 20, padding: 32 }}>
          {/* Toggle */}
          <div style={{ display: "flex", background: "var(--navy-mid)", borderRadius: 10, padding: 4, marginBottom: 24 }}>
            {["login", "register"].map(m => (
              <button key={m} onClick={() => { setMode(m); setError(""); }}
                style={{ flex: 1, padding: "8px", borderRadius: 8, background: mode === m ? "var(--surface)" : "transparent", border: "none", color: mode === m ? "var(--text)" : "var(--text-muted)", cursor: "pointer", fontSize: "0.875rem", fontWeight: 500, transition: "all 0.2s", fontFamily: "var(--font-body)" }}>
                {m === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>

          {/* Social buttons */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
            <button onClick={() => handleSocial("google")} disabled={loading} className="btn btn-secondary w-full" style={{ justifyContent: "center", gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/><path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"/><path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/><path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/></svg>
              Continue with Google
            </button>
            <button onClick={() => handleSocial("facebook")} disabled={loading} className="btn btn-secondary w-full" style={{ justifyContent: "center", gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Continue with Facebook
            </button>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "16px 0" }}>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            <span style={{ fontSize: "0.75rem", color: "var(--text-dim)" }}>or continue with email</span>
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>

          {/* Email form */}
          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleEmail}>
            {mode === "register" && (
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input name="name" className="form-input" placeholder="Your full name" value={form.name} onChange={handleChange} required />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input name="email" type="email" className="form-input" placeholder="you@example.com" value={form.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input name="password" type="password" className="form-input" placeholder={mode === "register" ? "Min. 6 characters" : "Your password"} value={form.password} onChange={handleChange} required minLength={6} />
            </div>
            <button type="submit" className="btn btn-primary w-full" style={{ justifyContent: "center", marginTop: 4 }} disabled={loading}>
              {loading ? "Please wait..." : mode === "login" ? "Sign In →" : "Create Account →"}
            </button>
          </form>
        </div>

        <p style={{ textAlign: "center", marginTop: 20, fontSize: "0.8125rem", color: "var(--text-dim)" }}>
          By continuing, you agree to our{" "}
          <Link to="/terms" style={{ color: "var(--accent)" }}>Terms</Link> and{" "}
          <Link to="/privacy" style={{ color: "var(--accent)" }}>Privacy Policy</Link>
        </p>
      </motion.div>
    </div>
  );
}
